import React from "react";
import { IGOMAveragesSet } from "../../../API/GOMAverages/GOMAveragesSet";
import { MainSite } from "../../../MainSite";
import { ParameterGroupInfoSet } from "../../../ParameterGroupInfoSet";
import { SiteObservations } from "../../SiteObservations/SiteObservations";
import { TableTimeCell } from "./TableTimeCell";
import { TableValueCell } from "./TableValueCell";

interface Props {
    mainSite: MainSite,
    data: IGOMAveragesSet,
    timeRange: "LatestValue" | "Average3h" | "Average24h",
}

export function Table(props: Props): React.JSX.Element {

    //const [dataType, setDataType] = React.useState

    //// The parameters of the table are organised in blocks
    const parameterGroupBlocks = {
        "Surface current": [
            ParameterGroupInfoSet.Total_CurrentSpeed_Surface_mps_Mean,
            ParameterGroupInfoSet.Total_CurrentDirection_Surface_deg_Mean
        ],
        "Peak current": [
            ParameterGroupInfoSet.Total_CurrentSpeed_Peak_mps_Mean,
            ParameterGroupInfoSet.Total_CurrentDirection_Peak_deg_Mean,
            ParameterGroupInfoSet.MeasurementDepth_Total_Current_Peak_deg_Mean
        ],
        "Wind": [
            ParameterGroupInfoSet.WindSpeed_kts,
            ParameterGroupInfoSet.WindDirection_deg
        ],
        "Wave": [
            ParameterGroupInfoSet.Hs_m,
            ParameterGroupInfoSet.Hmax_m,
            ParameterGroupInfoSet.Tp_s,
            ParameterGroupInfoSet.PeakWaveDirection_deg
        ]
    }

    //// Get unitsConverter
    // Not sure why we need this rather than just taking the units from the ParameterGroupInfoSets.
    // But this is how it is done on the TablePage.ts
    const unitsConverter = MainSite.getUnitConverter();

    //// Sort station Ids and labels
    const stationIds = props.data.Stations.map(station => station.Station.Id);

    const stationLabels = stationIds.map(stationId => props.data.Stations.filter(station => station.Station.Id == stationId)[0].Station.Label);

    const stationLabelsSorted = stationLabels.sort();

    let stationIdsSorted = [];

    for (const stationLabel of stationLabelsSorted) {

        for (const stationId of stationIds) {

            // Get corresponding Label
            const correspondingStationlabel = props.data.Stations.filter(station => station.Station.Id == stationId)[0].Station.Label

            if (correspondingStationlabel == stationLabel) {
                stationIdsSorted = [
                    ...stationIdsSorted,
                    stationId
                ]
            }
        }
    }

    return (
        <>
            <table className="table table-striped table-hover midi_tablePage_main_table midi_common_table GOM_table">
                <thead>

                    {/*Header row*/}
                    <tr className="midi_tablePage_main_table_header">

                        {/*Station*/}
                        <th>Station</th>

                        {/*latitude, longitude*/}
                        <th>Lat, Lon</th>

                        {Object.entries(parameterGroupBlocks).map(([label, parameterGroupBlock], idx) => {
                            return (

                                <React.Fragment key={idx}>
                                    {/* Add timestamp label for parameterGroupBlock */}
                                    <th>
                                        Latest Obs. Time
                                    </th>
                                    {parameterGroupBlock.map(parameterGroup => {
                                        // For each parameterGroup in the parameterGroupBlock, insert cell with name.
                                        return (
                                            <th
                                                title={parameterGroup.Description}
                                                key={parameterGroup.Id}
                                            >
                                                {parameterGroup.ShortName}
                                            </th>
                                        )
                                    })}
                                </React.Fragment>
                            )
                        })}
                    </tr>

                    {/*Units row*/}
                    <tr className="midi_tablePage_main_table_units">

                        {/*Station*/}
                        <th></th>

                        {/*latitude, longitude*/}
                        <th></th>

                        {Object.entries(parameterGroupBlocks).map(([label, parameterGroupBlock], idx) => {
                            return (
                                
                                <React.Fragment key={idx}>
                                    {/* Add timestamp for parameterGroupBlock */}
                                    <th>
                                        {props.mainSite.getTimeZoneString()}
                                    </th>
                                    {parameterGroupBlock.map(parameterGroup => {
                                        // For each parameterGroup in the parameterGroupBlock, insert cell with value.
                                        return (
                                            <th
                                                key={parameterGroup.Id}
                                            >
                                                {unitsConverter.getConvertedUnits(parameterGroup.Units, parameterGroup.Id)}
                                            </th>
                                        )
                                    })}
                                </React.Fragment>
                            )
                        })}
                    </tr>
                </thead>

                {/*Station rows*/}
                <tbody className="midi_tablePage_main_table_body">
                    {/* For each station, generate a row */}
                    {stationIdsSorted.map(stationId => {

                        //Object.entries(parameterGroupBlocks).map(([label, parameterGroupBlock], idx) => {
                        //    console.log(`[LOG: ${stationId}] idx: `, idx);
                        //    console.log(`[LOG: ${stationId}] label: `, label);
                        //    console.log(`[LOG: ${stationId}] parameterGroupBlock: `, parameterGroupBlock);
                        //})

                        const stationOi = props.data.Stations.filter(station => station.Station.Id == stationId)[0].Station;

                        return (
                            <tr 
                                data-siteid={stationId}
                                key={stationId}
                            >

                                {/* Station specs */}
                                <td
                                    className="midi_tablePage_main_table_body_small"
                                    title={`Click for ${stationOi.Label} station overview page.`}
                                    onClick={(e) => {
                                        props.mainSite.changePage('siteObs', {
                                            Station: stationId,
                                            Tab: SiteObservations.getLandingPageId(stationOi)
                                        });
                                    }}
                                >
                                    {stationOi.Label}
                                </td>
                                <td
                                    className="midi_tablePage_main_table_body_small GOM_table_coordinates"
                                >
                                    {stationOi.Latitude.toFixed(2)}, {stationOi.Longitude.toFixed(2)}
                                </td>

                                {/* For each parameterGroupBlock, generate cells */}
                                {Object.entries(parameterGroupBlocks).map(([label, parameterGroupBlock], idx) => {

                                    return (
                                        // Add timestamp for parameterGroupBlock
                                        <React.Fragment key={idx}>
                                                {
                                                    props.mainSite.liveDataManager.latestData 
                                                    ?
                                                <TableTimeCell
                                                    mainSite={props.mainSite}
                                                    station={stationOi}
                                                    parameterGroupBlock={parameterGroupBlock}
                                                    key={`time-${idx}`}
                                            />
                                                    :
                                                    <></>
                                                }
                                            {parameterGroupBlock.map(parameterGroup => {
                                                // For each parameterGroup in the parameterGroupBlock, insert cell with value.
                                                return (
                                                        props.mainSite.liveDataManager.latestData 
                                                        ? 
                                                    <TableValueCell
                                                        mainSite={props.mainSite}
                                                        station={stationOi}
                                                        parameterGroupId={parameterGroup.Id}
                                                        timeRange={props.timeRange}
                                                        key={parameterGroup.Id}
                                                    />
                                                        :
                                                        <></>
                                                )
                                            })}
                                        </React.Fragment>
                                    )
                                })}
                            </tr>
                        )
                    })}
                </tbody>
                                
            </table>

        </>
    );
}